import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { message } from "antd";
import { api } from "../../services/backend/api";
import PerformanceView from "./PerformanceView";
import {
  getWeekEndingDateBy,
  getDefaultDateRange,
} from "../../utils/formatters";

// Fill missing dates in data
const fillMissingDates = (data, start, end) => {
  // Get array of all dates in the range
  const dates = [];
  for (
    let date = new Date(start);
    date <= new Date(end);
    date.setDate(date.getDate() + 1)
  ) {
    dates.push(date.toISOString().split("T")[0]);
  }

  let dataPivotInd = 0;
  let missingDates = [];

  for (let i = 0; i < dates.length; i++) {
    if (dataPivotInd === data.length - 1 || data.length === 0) {
      if (dates[i] !== data[dataPivotInd]?.working_date) {
        missingDates.push(dates[i]);
      }
      continue;
    }

    for (let j = dataPivotInd; j < data.length; j++) {
      const a = Date.parse(dates[i]);
      const b = Date.parse(data[j].working_date);

      if (a - b === 0) {
        dataPivotInd = j;
      } else {
        const missing_days = (b - a) / (1000 * 60 * 60 * 24) - 1;
        if (missing_days > 0) {
          for (let k = 1; k <= missing_days; k++) {
            missingDates.push(dates[i + k]);
          }
          dataPivotInd = j;
          i += missing_days;
          break;
        } else {
          dataPivotInd = j;
          break;
        }
      }
    }
  }
  for (let i = 0; i < missingDates.length; i++) {
    data.unshift({
      working_date: missingDates[i],
      week_ending_date: getWeekEndingDateBy(missingDates[i]),
      cp_current_week_hours: 0,
      cp_current_week_units: 0,
      average_quality: 0,
      survivors: 0,
      hours_delivered: 0,
      units_delivered: 0,
      isDummyRow: true,
    });
  }

  if (data?.[0]?.working_date !== start) {
    data.unshift({
      working_date: start,
      week_ending_date: getWeekEndingDateBy(start),
      cp_current_week_hours: 0,
      cp_current_week_units: 0,
      average_quality: 0,
      survivors: 0,
      hours_delivered: 0,
      units_delivered: 0,
      isDummyRow: true,
    });
  }
  return data;
};

const calculateOverview = (data) => {
  if (!data || data.length === 0) {
    return {
      totalContributors: 0,
      totalHours: 0,
      totalCosts: 0,
      averageHoursPerWeek: 0,
      averageCostPerWeek: 0,
      averageHoursPerDay: 0,
      averageCostPerDay: 0,
    };
  }
  const overview = {
    totalContributors: 0,
    uniqueContributors: 0,
    totalHours: 0,
    averageHoursPerDay: 0,
    averageHoursPerWeek: 0,
  };
  const uniqueDates = new Set();
  const uniqueContributors = new Set();

  data.forEach((d) => {
    overview.totalHours += d.total_hours || 0;
    uniqueDates.add(d.working_date);
    if (!d.isDummyRow) {
      uniqueContributors.add(d.contributor_id);
      overview.totalContributors += 1;
    }
  });

  const days = Math.max(1, uniqueDates.size);
  const weeks = Math.max(1, Math.ceil(days / 7));
  overview.averageHoursPerDay = overview.totalHours / days;
  overview.averageHoursPerWeek = overview.totalHours / weeks;
  overview.uniqueContributors = uniqueContributors.size;
  return overview;
};

const processDailyData = (dailyData) => {
  const dateMap = new Map();
  dailyData.forEach((record) => {
    const date = record.working_date;
    if (!dateMap.has(date)) {
      dateMap.set(date, {
        date,
        totalContributors: 0,
        totalHours: 0,
        totalUnits: 0,
        averageRph: 0,
        averageQuality: 0,
      });
    }
    const dateData = dateMap.get(date);
    dateData.totalHours += record.total_hours || 0;
    dateData.totalUnits += record.total_units_completed || 0;
    dateData.averageQuality += record.average_quality || 0;
    if (!record.isDummyRow) dateData.totalContributors += 1;
  });
  return Array.from(dateMap.entries())
    .map(([date, data], index) => ({
      key: `day-${index}`,
      date: date,
      contributors: data.totalContributors,
      totalHours: Number(data.totalHours.toFixed(2)),
      totalUnits: Number(data.totalUnits.toFixed(2)),
      averageRph: Number((data.totalUnits / (data.totalHours || 1)).toFixed(2)),
      averageQuality: Number(data.averageQuality.toFixed(2)),
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

const aggregateWeeklyData = (dailyData) => {
  const weeklyMap = new Map();
  const uniqueContributorsInWeek = new Set();

  dailyData.forEach((record) => {
    const weekEnd = record.week_ending_date;
    if (!weeklyMap.has(weekEnd)) {
      weeklyMap.set(weekEnd, {
        date: weekEnd,
        uniqueContributors: 0,
        totalHours: 0,
        totalUnits: 0,
        averageRph: 0,
        averageQuality: 0,
      });
    }

    if (!record.isDummyRow) {
      if (
        !uniqueContributorsInWeek.has(`${weekEnd}-${record.contributor_id}`)
      ) {
        const weekData = weeklyMap.get(weekEnd);
        weekData.totalHours += record.cp_current_week_hours || 0;
        weekData.totalUnits += record.cp_current_week_units || 0;
        weekData.averageQuality += record.weekly_average_quality || 0;
        weekData.uniqueContributors += 1;
        uniqueContributorsInWeek.add(`${weekEnd}-${record.contributor_id}`);
      }
    }
  });
  const res = Array.from(weeklyMap.entries())
    .map(([weekEnd, data], index) => ({
      key: `week-${index}`,
      date: weekEnd,
      contributors: data.uniqueContributors,
      totalHours: Number(data.totalHours.toFixed(2)),
      totalUnits: Number(data.totalUnits.toFixed(2)),
      averageRph: Number((data.totalUnits / (data.totalHours || 1)).toFixed(2)),
      averageQuality: Number(data.averageQuality.toFixed(2)),
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  return res;
};

const PerformanceOrchestrator = ({
  projectId: pIdProp,
  objectiveId: oIdProp,
  timeframeDateRange = {},
}) => {
  const [searchParams] = useSearchParams();
  const projectId = pIdProp || searchParams.get("projectId");
  const objectiveId = oIdProp || searchParams.get("objectiveId");

  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [isWeekly, setIsWeekly] = useState(true);
  const [activeTab, setActiveTab] = useState("charts");
  const [objectiveMetrics, setObjectiveMetrics] = useState([]);
  const [poMetadata, setPoMetadata] = useState(null);
  const [cohortViews, setCohortViews] = useState([]);
  const [cohortViewsLoading, setCohortViewsLoading] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [contributorMetricsRefreshTrigger, setContributorMetricsRefreshTrigger] =
    useState(0);

  const overview = useMemo(
    () => calculateOverview(objectiveMetrics),
    [objectiveMetrics],
  );

  const tableData = useMemo(
    () =>
      objectiveMetrics.length === 0
        ? { weekly: [], daily: [] }
        : {
            weekly: aggregateWeeklyData(objectiveMetrics),
            daily: processDailyData(objectiveMetrics),
          },
    [objectiveMetrics],
  );

  const [selectedChartDate, setSelectedChartDate] = useState(null);

  const loadMetadata = useCallback(
    async (getIsCancelled = () => false) => {
      try {
        let metadataData = null;
        if (objectiveId) {
          const storedObjectives = JSON.parse(
            localStorage.getItem("connectedProjectObjectives") || "[]",
          );
          metadataData = storedObjectives.find((o) => o.id === objectiveId);
          if (!metadataData) {
            try {
              const objectives = await api.get(
                `/project/connected/${objectiveId}/objectives`,
              );
              if (getIsCancelled()) return;
              if (Array.isArray(objectives) && objectives.length > 0) {
                metadataData =
                  objectives.find((o) => o.id === objectiveId) || objectives[0];
              }
            } catch (error) {
              if (getIsCancelled()) return;
              console.error("Error fetching objective metadata:", error);
            }
          }
        } else if (projectId) {
          const storedProjects = JSON.parse(
            localStorage.getItem("connectedProjects") || "[]",
          );
          metadataData = storedProjects.find((p) => p.id === projectId);
          if (!metadataData) {
            try {
              const projects = await api.get("/project/connected/all");
              if (getIsCancelled()) return;
              if (Array.isArray(projects)) {
                metadataData = projects.find((p) => p.id === projectId);
              }
            } catch (error) {
              if (getIsCancelled()) return;
              console.error("Error fetching project metadata:", error);
            }
          }
        }
        if (getIsCancelled()) return;
        if (metadataData) {
          setMetadata({
            id: metadataData.id,
            name:
              metadataData.name ||
              metadataData.project_objective_name_c ||
              "Unknown",
            type: objectiveId ? "objective" : "project",
            status:
              metadataData.status_c ||
              metadataData.project_status_c ||
              "Unknown",
            client: metadataData.client_name_c || metadataData.orgname || "N/A",
            description:
              metadataData.project_objective_description_c ||
              metadataData.project_short_description_c ||
              "N/A",
            createdDate: metadataData.created_date || metadataData.created_at,
            programname: metadataData.programname,
            taskid: metadataData.taskid,
          });
        } else {
          setMetadata({
            id: projectId || objectiveId,
            name: projectId ? "Project" : "Objective",
            type: projectId ? "project" : "objective",
            status: "Unknown",
            client: "N/A",
            description: "Metadata not available",
            createdDate: new Date().toISOString(),
          });
        }
      } catch (error) {
        if (getIsCancelled()) return;
        console.error("Error loading metadata:", error);
        message.error("Failed to load metadata");
      }
    },
    [projectId, objectiveId],
  );

  const fetchPOMetadata = useCallback(async (id) => {
    if (!id) return null;
    try {
      return await api.get(`/project/connected/objective/${id}`);
    } catch (err) {
      console.error("Error fetching PO metadata:", err);
      return null;
    }
  }, []);

  // Load metadata + PO metadata when project/objective changes
  useEffect(() => {
    if (!projectId && !objectiveId) return;
    let cancelled = false;
    const getIsCancelled = () => cancelled;

    if (objectiveId) {
      setPoMetadata(null);
      fetchPOMetadata(objectiveId).then((data) => {
        if (!cancelled) setPoMetadata(data);
      });
    } else {
      setPoMetadata(null);
    }
    loadMetadata(getIsCancelled);

    return () => {
      cancelled = true;
    };
  }, [projectId, objectiveId, loadMetadata, fetchPOMetadata]);

  const loadPerformanceData = useCallback(
    (options = {}) => {
      const { start, end } = timeframeDateRange;
      if (!start || !end || (!objectiveId && !projectId)) return;

      const base = objectiveId
        ? `/performance/po/weekly/connected/${objectiveId}?start=${start}&end=${end}`
        : `/performance/project/weekly/connected/${projectId}?start=${start}&end=${end}`;
      const url =
        options.refresh === true
          ? `${base}&refresh=true&_=${Date.now()}`
          : base;

      setLoading(true);
      setObjectiveMetrics([]);
      api
        .get(url)
        .then((res) => {
          const raw = Array.isArray(res) ? res : res?.data;
          const data = fillMissingDates(
            Array.isArray(raw) ? raw : [],
            start,
            end,
          );
          setObjectiveMetrics(data);
          const synced = res?.last_synced_at ?? null;
          setLastSyncedAt(synced ? new Date(synced) : null);
        })
        .catch((err) => {
          console.error("Error loading performance data:", err);
          message.error("Failed to load performance data");
          setObjectiveMetrics([]);
        })
        .finally(() => setLoading(false));
    },
    [timeframeDateRange, objectiveId, projectId],
  );

  const handleRefreshPerformance = useCallback(() => {
    loadPerformanceData({ refresh: true });
  }, [loadPerformanceData]);

  const handleRefreshContributorMetrics = useCallback(() => {
    setContributorMetricsRefreshTrigger((t) => t + 1);
  }, []);

  useEffect(() => {
    loadPerformanceData();
  }, [loadPerformanceData]);

  // Reset chart date selection when timeframe changes
  useEffect(() => {
    setSelectedChartDate(null);
  }, [timeframeDateRange.start, timeframeDateRange.end]);

  const handleSelectChartDate = useCallback((date) => {
    setSelectedChartDate((prev) =>
      date == null ? null : date === prev ? null : date,
    );
  }, []);

  const handleToggleWeekly = useCallback((checked) => {
    setIsWeekly(checked);
  }, []);

  const handleActiveTabChange = useCallback((key) => {
    setActiveTab(key);
  }, []);

  return (
    <PerformanceView
      projectId={projectId}
      objectiveId={objectiveId}
      loading={loading}
      metadata={metadata}
      overview={overview}
      tableData={tableData}
      chartData={tableData}
      rawData={objectiveMetrics}
      isWeekly={isWeekly}
      activeTab={activeTab}
      poMetadata={poMetadata}
      selectedChartDate={selectedChartDate}
      lastSyncedAt={lastSyncedAt}
      onRefreshPerformance={handleRefreshPerformance}
      onRefreshContributorMetrics={handleRefreshContributorMetrics}
      contributorMetricsRefreshTrigger={contributorMetricsRefreshTrigger}
      onSelectChartDate={handleSelectChartDate}
      onActiveTabChange={handleActiveTabChange}
      onToggleWeekly={handleToggleWeekly}
      timeframeDateRange={timeframeDateRange}
    />
  );
};

export default PerformanceOrchestrator;
